<template>
  <v-container class="free-tool">
    <div class="free-tool__hero">
      <header class="free-tool__header">
        <h1 class="text-h4 text-md-h3 font-weight-light">{{ title }}</h1>
        <p class="text-body-1 text-medium-emphasis">{{ intro }}</p>
      </header>

      <!-- The tool itself (client-only console + skeleton fallback, from the page). -->
      <div class="free-tool__console">
        <slot />
      </div>
    </div>

    <section class="free-tool__features" aria-label="Features">
      <div v-for="feature in features" :key="feature.title" class="free-tool__feature">
        <h2 class="text-subtitle-1 font-weight-medium">{{ feature.title }}</h2>
        <p class="text-body-2 text-medium-emphasis">{{ feature.body }}</p>
      </div>
    </section>

    <FaqAccordion :groups="[{ title: 'Questions', items: faq }]" />

    <div class="free-tool__closing">
      <v-card variant="tonal" color="primary" rounded="lg" class="free-tool__cta">
        <h2 class="text-h6 font-weight-medium">{{ ctaTitle }}</h2>
        <p class="text-body-2">{{ ctaBody }}</p>
        <v-btn color="primary" variant="flat" size="large" to="/app">Try Song7 free</v-btn>
      </v-card>

      <p class="text-body-2 text-medium-emphasis text-center">
        <slot name="related" />
      </p>
    </div>
  </v-container>
</template>

<script setup lang="ts">
// Shared layout for the free standalone tools (/metronome, /tuner): intro, the
// tool, three feature notes, questions, and a closing invitation. Spacing lives
// here so both pages keep the same rhythm.
defineProps<{
  title: string
  intro: string
  features: Array<{ title: string; body: string }>
  faq: Array<{ question: string; answer: string }>
  ctaTitle: string
  ctaBody: string
}>()
</script>

<style scoped>
.free-tool {
  max-width: 1100px;
  display: flex;
  flex-direction: column;
  /* Even breathing room between sections; tighter on phones. */
  gap: clamp(64px, 8vw, 96px);
  padding-block: clamp(48px, 7vw, 88px) clamp(64px, 8vw, 104px);
}

/* The intro and its tool read as one unit, so they sit closer than sections do. */
.free-tool__hero {
  display: grid;
  gap: clamp(32px, 5vw, 56px);
}

.free-tool__header {
  display: grid;
  gap: 16px;
  text-align: center;
}

.free-tool__header h1 {
  margin: 0;
  text-wrap: balance;
}

.free-tool__header p {
  max-width: 620px;
  margin: 0 auto;
  line-height: 1.7;
}

.free-tool__console {
  display: flex;
  justify-content: center;
}

.free-tool__features {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 40px 48px;
}

.free-tool__feature {
  display: grid;
  align-content: start;
  gap: 8px;
}

.free-tool__feature h2,
.free-tool__feature p {
  margin: 0;
}

.free-tool__feature p {
  line-height: 1.7;
}

.free-tool__closing {
  display: grid;
  gap: 32px;
}

.free-tool__cta {
  display: grid;
  justify-items: center;
  gap: 12px;
  padding: clamp(32px, 5vw, 48px) 24px;
  text-align: center;
}

.free-tool__cta h2,
.free-tool__cta p {
  margin: 0;
}

.free-tool__cta p {
  max-width: 560px;
  line-height: 1.7;
  margin-bottom: 8px;
}

.free-tool__closing > p {
  margin: 0;
}
</style>
